import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastMessageLevel, ToastProps } from '../toast';
import { create } from 'zustand';

type GlobalToastStore = ToastProps & {
  // 既定文言での上書きから保護するメッセージか（代行ログインの操作拒否など）
  isProtected: boolean;
  setMessage: (message: string, messageLevel: ToastMessageLevel) => void;
};

const useGlobalToastStore = create<GlobalToastStore>((set) => ({
  isActive: false,
  label: '',
  messageLevel: ToastMessageLevel.Error,
  isProtected: false,
  setIsActive: (isActive: boolean) => set(() => ({ isActive })),
  // 新しいメッセージ表示時に保護フラグはリセット（明示的に保護する場合のみ後で立てる）
  setMessage: (message: string, messageLevel: ToastMessageLevel) =>
    set(() => ({ label: message, isActive: true, messageLevel, isProtected: false })),
}));

// 現在表示中のトーストを「空の showError で上書きされない」よう保護する。
// router.tsx の onError が代行ログインの操作拒否メッセージを表示した直後に呼ぶ。
export const markCurrentToastProtected = () => {
  useGlobalToastStore.setState({ isProtected: true });
};

export const useGlobalToast = (): ToastProps => {
  const isActive = useGlobalToastStore((state) => state.isActive);
  const label = useGlobalToastStore((state) => state.label);
  const messageLevel = useGlobalToastStore((state) => state.messageLevel);
  const setIsActive = useGlobalToastStore((state) => state.setIsActive);

  console.log('isActive', isActive);

  return {
    isActive,
    label,
    messageLevel,
    setIsActive,
  };
};

export const useGlobalToastHandler = () => {
  const location = useLocation();
  const setIsActive = useGlobalToastStore((state) => state.setIsActive);
  const setMessage = useGlobalToastStore((state) => state.setMessage);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  const showMessage = useCallback((message: string, messageLevel: ToastMessageLevel, durationMs?: number) => {
    // 連続で呼ばれた場合に意図せずに非表示になるのでclearTimeoutする
    clearTimeout(timeoutIdRef.current);
    setMessage(message, messageLevel);

    if (durationMs) {
      timeoutIdRef.current = setTimeout(() => {
        setIsActive(false);
      }, durationMs);
    }
  }, []);

  const showInfo = useCallback((message: string) => {
    showMessage(message, ToastMessageLevel.Info, 4000);
  }, []);

  const showWarn = useCallback((message: string) => {
    showMessage(message, ToastMessageLevel.Warn, 4000);
  }, []);

  const showError = useCallback((message: string | null | undefined, durationMs?: number) => {
    // メッセージ未指定の場合、保護中のメッセージ（onErrorで表示し markCurrentToastProtected で保護した
    // 代行ログインの操作拒否など）が表示中なら、既定文言で上書きしない。
    // それ以外は従来どおり既定文言を表示する（代行ログイン以外の挙動は不変）。
    if (!message) {
      const state = useGlobalToastStore.getState();
      if (state.isActive && state.isProtected) {
        return;
      }
    }
    showMessage(message || 'エラーが発生しました', ToastMessageLevel.Error, durationMs);
  }, []);

  const messageClose = useCallback(() => {
    setIsActive(false);
  }, [setIsActive]);

  // 画面遷移した場合は非表示にする
  useEffect(() => {
    clearTimeout(timeoutIdRef.current);
    setIsActive(false);
  }, [location]);

  return {
    showInfo,
    showError,
    showWarn,
    messageClose,
  };
};
